// src/pages/EditCampaignPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase/client';
import TargetAudienceSelector from '../components/TargetAudienceSelector';
import type { TargetAudienceData } from '../hooks/useTargetAudience';
import type { Campaign } from '../types/Campaign';

const EditCampaignPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Using direct supabase import instead of useSupabaseClient
  
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [targetAudienceData, setTargetAudienceData] = useState<TargetAudienceData>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load campaign data
  useEffect(() => {
    const loadCampaign = async () => {
      if (!id) return;

      try {
        console.log('EditCampaignPage: Loading campaign with ID:', id);
        
        const { data, error } = await supabase
          .from('campaigns')
          .select('*')
          .eq('id', id)
          .single();

        console.log('EditCampaignPage: Campaign query result:', { data, error });

        if (error) throw error;

        setCampaign(data);
        
        // Set initial target audience data if it exists
        const initialData: TargetAudienceData = {};
        if (data.target_audience_type) {
          initialData.type = data.target_audience_type;
        }
        if (data.target_audience_filter) {
          initialData.filter = data.target_audience_filter;
        }
        if (data.target_audience_file_url) {
          initialData.fileUrl = data.target_audience_file_url;
          // Extract filename from URL or use default
          const urlParts = data.target_audience_file_url.split('/');
          initialData.fileName = urlParts[urlParts.length - 1] || 'uploaded-file.csv';
        }
        setTargetAudienceData(initialData);
      } catch (err) {
        console.error('EditCampaignPage: Error loading campaign:', err);
        setError(err instanceof Error ? err.message : 'Failed to load campaign');
      } finally {
        setLoading(false);
      }
    };

    loadCampaign();
  }, [id]);

  const handleSave = async () => {
    if (!id) return;

    setSaving(true);
    setError(null);

    try {
      const updateData: {
        target_audience_type: string | null;
        target_audience_filter: object | null;
        target_audience_file_url: string | null;
      } = {
        target_audience_type: targetAudienceData.type || null,
        target_audience_filter: targetAudienceData.filter || null,
        target_audience_file_url: targetAudienceData.fileUrl || null,
      };

      const { error } = await supabase
        .from('campaigns')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      // Navigate back to home
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading campaign...</p>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Campaign not found</h2>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Campaign</h1>
              <p className="mt-2 text-gray-600">
                Campaign: <span className="font-medium">{campaign.name}</span>
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleCancel}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !targetAudienceData.type}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Target Audience Selector */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Target Audience Selection</h2>
            <p className="text-gray-600">Choose how you want to target your audience for this campaign.</p>
          </div>
          
          <TargetAudienceSelector
            campaignId={id!}
            onChange={setTargetAudienceData}
            initialData={targetAudienceData}
          />
        </div>
      </div>
    </div>
  );
};

export default EditCampaignPage;
